import { useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Textarea,
  Button,
  Stack,
  Group,
  Loader,
  Alert,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateAgendaReturnForm,
  useUpdateAgendaReturnForm,
  useMeeting,
  useAgendaReturnForm,
} from "../hooks";
import { AgendaItem } from "../../../api/bidCommittee";
import { IconAlertCircle } from "@tabler/icons-react";
import { syncThaanaFieldsFromDom, useThaanaFormSync } from "../../../hooks/useThaanaFormSync";

interface FormValues {
  meetingId: string;
  agendaItemId: string;
  reasonsForReturning: string;
  detailsOfAmendmentsRequired: string;
  allowedToResubmitWithAmendments: string;
}

export function AgendaReturnFormForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  const { data: returnForm, isLoading: formLoading } = useAgendaReturnForm(id || "");
  const resolvedMeetingId = searchParams.get("meetingId") || returnForm?.meetingId || "";
  const { data: meeting, isLoading: meetingLoading } = useMeeting(resolvedMeetingId || undefined);
  const createForm = useCreateAgendaReturnForm();
  const updateForm = useUpdateAgendaReturnForm();

  const form = useForm<FormValues>({
    initialValues: {
      meetingId: resolvedMeetingId,
      agendaItemId: "",
      reasonsForReturning: "",
      detailsOfAmendmentsRequired: "",
      allowedToResubmitWithAmendments: "",
    },
    validate: {
      meetingId: (v) => (!v ? "Meeting is required" : null),
      agendaItemId: (v) => (!v ? "Agenda item is required" : null),
      reasonsForReturning: (v) => (!v?.trim() ? "Required (Dhivehi)" : null),
      detailsOfAmendmentsRequired: (v) => (!v?.trim() ? "Required (Dhivehi)" : null),
      allowedToResubmitWithAmendments: (v) => (!v?.trim() ? "Required (Dhivehi)" : null),
    },
  });

  useThaanaFormSync(form);

  const eligibleAgendaItems =
    meeting?.agendaItems?.filter((item: AgendaItem) => {
      const hasProcurement =
        item.tenderId || item.singleSourceId || item.projectSummarySheetId;
      const hasReturnForm = item.agendaReturnForm?.id;
      if (!hasProcurement) return false;
      if (hasReturnForm && (!isEditMode || item.agendaReturnForm?.id !== id)) return false;
      return true;
    }) ?? [];

  const agendaOptions = eligibleAgendaItems.map((item) => {
    let ref = "";
    if (item.tender) ref = item.tender.referenceNo;
    else if (item.singleSource) ref = item.singleSource.formNumber;
    else if (item.projectSummarySheet) ref = item.projectSummarySheet.summarySheetNumber;
    return {
      value: item.id,
      label: `#${item.orderNo} — ${item.subject}${ref ? ` (${ref})` : ""}`,
    };
  });

  useEffect(() => {
    if (resolvedMeetingId) form.setFieldValue("meetingId", resolvedMeetingId);
  }, [resolvedMeetingId]);

  useEffect(() => {
    if (isEditMode && returnForm) {
      form.setValues({
        meetingId: returnForm.meetingId,
        agendaItemId: returnForm.agendaItemId,
        reasonsForReturning: returnForm.reasonsForReturning,
        detailsOfAmendmentsRequired: returnForm.detailsOfAmendmentsRequired,
        allowedToResubmitWithAmendments: returnForm.allowedToResubmitWithAmendments,
      });
    }
  }, [isEditMode, returnForm]);

  const handleSubmit = (values: FormValues) => {
    const payload = {
      meetingId: values.meetingId,
      agendaItemId: values.agendaItemId,
      reasonsForReturning: values.reasonsForReturning,
      detailsOfAmendmentsRequired: values.detailsOfAmendmentsRequired,
      allowedToResubmitWithAmendments: values.allowedToResubmitWithAmendments,
    };
    if (isEditMode && id) {
      updateForm.mutate(
        { id, data: payload },
        { onSuccess: () => navigate(`/bid-committee/agenda-return-forms/${id}`) }
      );
    } else {
      createForm.mutate(payload, {
        onSuccess: (res) => navigate(`/bid-committee/agenda-return-forms/${res.data.id}`),
      });
    }
  };

  if (meetingLoading || (isEditMode && formLoading)) {
    return (
      <Container size="md" py="xl">
        <Loader />
      </Container>
    );
  }

  if (!meeting) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle />} color="red">
          Meeting not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="lg">
        {isEditMode ? "Edit agenda return form" : "New agenda return form"}
      </Title>
      <Text c="dimmed" mb="md">
        Meeting: {meeting.title} ({meeting.meetingNumber})
      </Text>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          syncThaanaFieldsFromDom(form);
          form.onSubmit(handleSubmit)(e);
        }}
      >
        <Stack gap="md">
          <Card withBorder p="md">
            <Select
              label="Agenda item"
              placeholder="Select agenda item"
              data={agendaOptions}
              value={form.values.agendaItemId}
              onChange={(v) => form.setFieldValue("agendaItemId", v || "")}
              required
              disabled={isEditMode}
              searchable
            />
            {agendaOptions.length === 0 && (
              <Text size="sm" c="dimmed" mt="xs">
                No eligible agenda items. Each item needs a tender, single-source, or PR-03 link and
                must not already have a return form.
              </Text>
            )}
          </Card>

          <Card withBorder p="md">
            <Stack gap="md">
              <div className="thaana-input">
                <Textarea
                  label="Reasons for returning (Dhivehi)"
                  name="reasonsForReturning"
                  minRows={4}
                  value={form.values.reasonsForReturning}
                  onChange={(e) => form.setFieldValue("reasonsForReturning", e.currentTarget.value)}
                  required
                />
              </div>
              <div className="thaana-input">
                <Textarea
                  label="Details of amendments required (Dhivehi)"
                  name="detailsOfAmendmentsRequired"
                  minRows={4}
                  value={form.values.detailsOfAmendmentsRequired}
                  onChange={(e) =>
                    form.setFieldValue("detailsOfAmendmentsRequired", e.currentTarget.value)
                  }
                  required
                />
              </div>
              <div className="thaana-input">
                <Textarea
                  label="Allowed to resubmit with amendments (Dhivehi)"
                  name="allowedToResubmitWithAmendments"
                  minRows={3}
                  value={form.values.allowedToResubmitWithAmendments}
                  onChange={(e) =>
                    form.setFieldValue("allowedToResubmitWithAmendments", e.currentTarget.value)
                  }
                  required
                />
              </div>
            </Stack>
          </Card>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" loading={createForm.isPending || updateForm.isPending}>
              {isEditMode ? "Save changes" : "Create form"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
